// Owner: data-model-objects (plan §9.14, §4.2). Keys of the object builder's field table: ↑/↓ (or
// J/K) move, Enter opens the field, X selects, ⌘A selects all, ⌘D duplicates, Delete archives,
// Esc clears the selection, ⌘Z undoes the last paste, duplicate or AI field. ⌘C/⌘V go through the
// clipboard service (FieldsTab). Active while focus is in the table. Keep this file tiny.
import { defineCommand, defineScope } from '@dataroom/interaction';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';

export type FieldTableContext = {
  move: (delta: 1 | -1) => boolean;
  open: () => boolean;
  toggle: () => boolean;
  selectAll: () => boolean;
  clear: () => boolean;
  duplicate: () => boolean;
  archive: () => boolean;
  undo: () => boolean;
  canEdit: () => boolean;
};

export const fieldTableScope = defineScope<FieldTableContext>({
  id: 'dm.fields',
  kind: 'list',
  title: () => t('dm.keys.scope'),
});

export default defineAppCommands({
  id: 'data-model.fields',
  loadTitles: () => import('./messages'),
  scopes: [fieldTableScope],
  commands: [
    defineCommand<FieldTableContext>({
      id: 'dm.fields.next',
      title: () => t('dm.keys.next'),
      icon: 'chevron-down',
      shortcut: ['arrowdown', 'j'],
      scope: fieldTableScope,
      repeat: true,
      run: (ctx) => ctx.move(1),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.previous',
      title: () => t('dm.keys.previous'),
      icon: 'chevron-up',
      shortcut: ['arrowup', 'k'],
      scope: fieldTableScope,
      repeat: true,
      run: (ctx) => ctx.move(-1),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.open',
      title: () => t('dm.keys.open'),
      shortcut: 'enter',
      scope: fieldTableScope,
      run: (ctx) => ctx.open(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.toggle',
      title: () => t('dm.keys.toggle'),
      icon: 'check',
      shortcut: 'x',
      scope: fieldTableScope,
      run: (ctx) => ctx.toggle(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.selectAll',
      title: () => t('dm.keys.selectAll'),
      shortcut: 'mod+a',
      scope: fieldTableScope,
      run: (ctx) => ctx.selectAll(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.clear',
      title: () => t('dm.keys.clear'),
      shortcut: 'escape',
      scope: fieldTableScope,
      run: (ctx) => ctx.clear(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.duplicate',
      title: () => t('dm.keys.duplicate'),
      icon: 'copy',
      shortcut: 'mod+d',
      scope: fieldTableScope,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.duplicate(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.archive',
      title: () => t('dm.keys.archive'),
      icon: 'trash',
      shortcut: ['delete', 'backspace'],
      scope: fieldTableScope,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.archive(),
    }),
    defineCommand<FieldTableContext>({
      id: 'dm.fields.undo',
      title: () => t('dm.keys.undo'),
      icon: 'undo',
      shortcut: 'mod+z',
      scope: fieldTableScope,
      run: (ctx) => ctx.undo(),
    }),
  ],
});
